// Online play: a shared room, one game state, everybody watching it.
//
// Same shape as the thunderstone repo's remote play (this is a port of it):
// client-authoritative full-snapshot sync over Firebase RTDB, with every move
// going through the exact same engine.dispatch() as a local game. The only
// difference is where the resulting state gets written.
//
// Namespacing follows the house convention already in database.rules.json -
// `spaceBaseGames`/`spaceBaseGameCodes` beside `games`/`gameCodes`, so each
// game's rooms live in their own subtree and room codes can't collide across
// games.

import { deadAfterFor, waitingPatch } from './asyncPlay.js';
import {
  ref as dbRef, onValue, onDisconnect, push, set, get, update, remove, runTransaction,
} from 'firebase/database';
import { db } from '../firebase';
import { generateRoomCode, normalizeRoomCode } from '../utils/roomCodes';
import { createGameState } from '../engine/state/createGameState';
import { PHASES, MIN_PLAYERS, MAX_PLAYERS } from '../engine/constants';
import { PERSONALITIES, personalityFor } from '../engine/ai/heuristicBot';

/**
 * How long a room with nobody in it may sit before it is cleaned up.
 *
 * Presence is the real signal: Firebase clears it the instant a connection
 * drops, so an empty presence map means literally nobody has this room open.
 * The ages below are the safety margin on top of that - long enough that a
 * dropped phone or a walk to the kitchen never costs anyone a game.
 *
 * A lobby is the aggressive case because it holds nothing: if everyone has
 * closed it before dealing, there is nothing to lose. A game in progress gets
 * a full day, because "we'll finish this tomorrow" is a real thing people do.
 */
export const DEAD_AFTER_MS = {
  lobby: 60 * 60 * 1000,          // an hour
  'in-progress': 24 * 60 * 60 * 1000,
  finished: 2 * 60 * 60 * 1000,
};

/** Best-effort "when was anyone last here", tolerant of older rooms. */
export const lastActiveOf = (meta) => {
  if (Number.isFinite(meta?.lastActiveAt)) return meta.lastActiveAt;
  // Rooms created before lastActiveAt existed only have the ISO string.
  const parsed = Date.parse(meta?.createdAt || '');
  return Number.isFinite(parsed) ? parsed : 0;
};

/**
 * Is this room finished with?
 *
 * Two conditions, both required. Somebody being connected always saves a room,
 * whatever its age - that is the case where deleting it would actually hurt.
 */
export const isDeadRoom = (meta, presence, now = Date.now()) => {
  if (!meta) return false;
  if (Object.keys(presence || {}).length > 0) return false;
  const age = now - lastActiveOf(meta);
  // An async room (asyncPlay.js) is meant to sit for days; it gets the long limits.
  const limit = deadAfterFor(meta, DEAD_AFTER_MS);
  return age > limit;
};

export const clampTableSize = (n) => Math.min(Math.max(Number(n) || MIN_PLAYERS, MIN_PLAYERS), MAX_PLAYERS);

export const GAMES = 'cantStopGames';
export const CODES = 'cantStopGameCodes';
export const USER_GAMES = 'cantStopUserGames';

// How long a player can be gone before a bot starts playing their hand.
// Matt's call: a game shouldn't stall because one phone went in a pocket, and
// the seat stays theirs to reclaim the moment they come back.
export const DISCONNECT_GRACE_MS = 30000;

/**
 * The state is stored as a JSON STRING, not as a tree.
 *
 * RTDB has no representation for an empty array or empty object and silently
 * drops such keys - a player whose `cards: []` would come back with no
 * `cards` key at all, and every player starts that way. Stringifying keeps
 * the state byte-identical through the round trip. (Learned the hard way in
 * the thunderstone repo; its own sync carries the same note.)
 */
const encode = (state) => JSON.stringify(state);
const decode = (raw) => (raw ? JSON.parse(raw) : null);

export const seqOf = (state) => state?.meta?.seq ?? 0;

/**
 * Who is allowed to write the next move.
 *
 * A connected human plays their own turn. Everything else - a bot seat, or a
 * human whose connection has dropped past the grace period - has to be played
 * by SOMEBODY's browser, and exactly one, or the move gets applied twice.
 *
 * The rule: the connected human in the lowest seat drives. It's derived
 * purely from state every client already has, so all of them agree without
 * needing to negotiate, and it survives the host leaving (which a
 * "host drives" rule would not).
 *
 * The seq check in `commitAction` is the backstop for the brief windows where
 * two clients might still disagree about who is connected.
 */
export const driverUidFor = (gameState, presence) => {
  const connected = (uid) => Boolean(presence?.[uid]);
  const humans = (gameState?.players || []).filter((p) => !p.isBot && connected(p.id));
  if (humans.length === 0) return null;
  return humans.reduce((best, p) => (p.seat < best.seat ? p : best)).id;
};

/**
 * Is nobody at this seat to play it themselves?
 *
 * True for a bot, and for a human whose connection has been gone longer than
 * the grace period. False for a connected human - including you.
 */
export const seatNeedsPlayingFor = (gameState, presence, disconnectedSince, asyncRoom = false) => {
  const active = gameState?.players?.[gameState.meta.activePlayerIndex];
  if (!active) return false;
  if (active.isBot) return true;
  // Async play (asyncPlay.js): a human who is not here is not here yet. The game waits.
  if (asyncRoom) return false;
  if (presence?.[active.id]) return false;
  const since = disconnectedSince?.[active.id];
  return Boolean(since) && Date.now() - since >= DISCONNECT_GRACE_MS;
};

/**
 * Should THIS client auto-play the active seat right now?
 *
 * The two halves matter separately, and conflating them is what broke the
 * first online game Matt and Carrie played: "it seems like it's treating all
 * of us as bots and me included." The old single predicate answered "is this
 * client responsible for this seat", which is TRUE for your own turn - and
 * the caller took any true as licence to play a bot move. So every client
 * cheerfully bot-played its own human, and the game played itself.
 *
 * Auto-play requires BOTH that the seat has nobody to play it (a bot, or
 * someone who has dropped out) AND that this client is the designated driver.
 * A connected human's turn is never auto-played by anybody, ever.
 */
export const shouldAutoPlay = ({ gameState, presence, myUid, disconnectedSince, asyncRoom = false }) => {
  if (!gameState || gameState.meta.phase !== PHASES.PLAYING) return false;
  if (!seatNeedsPlayingFor(gameState, presence, disconnectedSince, asyncRoom)) return false;
  return driverUidFor(gameState, presence) === myUid;
};

/**
 * Is the active seat mine to play, with the buttons? Purely a UI question -
 * it never authorises an automatic move.
 */
export const isMyTurn = ({ gameState, presence, myUid }) => {
  if (!gameState || gameState.meta.phase !== PHASES.PLAYING) return false;
  const active = gameState.players[gameState.meta.activePlayerIndex];
  return Boolean(active && !active.isBot && active.id === myUid && presence?.[active.id] !== false);
};

/**
 * Which bot temperament plays a given seat. Real bot seats keep the
 * personality they were dealt; a stand-in for an absent human plays 'steady',
 * the middle one - taking over somebody's hand is not the moment to be
 * characterful with it.
 */
export const personalityForSeat = (player) => (
  player.isBot ? personalityFor(player.botIndex ?? 0) : 'steady'
);

/**
 * This game's async adapter (asyncPlay.js): a turn-based game waits on the
 * active seat; if that seat is a bot, on the human in the lowest seat, who
 * will drive the bots the moment they open the room.
 */
export const asyncAdapter = {
  waitingOnOf: (g) => {
    const active = g?.players?.[g.meta.activePlayerIndex];
    if (!active) return [];
    if (!active.isBot) return [active.id];
    const humans = (g.players || []).filter((p) => !p.isBot);
    return humans.length ? [humans.reduce((best, p) => (p.seat < best.seat ? p : best)).id] : [];
  },
  isOver: (g) => g?.meta?.phase === PHASES.FINISHED,
};

/** After a move in an async room: who the game waits on now (asyncPlay.js). */
export const recordWaiting = async ({ gameId, gameState, meta }) => {
  if (!db || !meta?.async || !gameState) return;
  await update(dbRef(db, `${GAMES}/${gameId}/meta`), waitingPatch(gameState, meta, asyncAdapter)).catch(() => {});
};

export const createRoom = async ({ user, tableSize = MIN_PLAYERS, async: asyncRoom = false }) => {
  if (!db) throw new Error('Online play needs Firebase configured (see .env.example).');
  if (!user) throw new Error('Sign in first.');

  const gameRef = push(dbRef(db, GAMES));
  const gameId = gameRef.key;

  let code;
  let attempts = 0;
  do {
    code = generateRoomCode(attempts);
    attempts += 1;
    // eslint-disable-next-line no-await-in-loop
  } while (attempts < 12 && (await get(dbRef(db, `${CODES}/${code}`))).exists());

  const meta = {
    hostUid: user.uid,
    status: 'lobby',
    roomCode: code,
    async: asyncRoom,
    createdAt: new Date().toISOString(),
    // Numeric twin of createdAt, refreshed whenever anyone shows up. The ISO
    // string stays because it's the one a human reads in a console.
    lastActiveAt: Date.now(),
    // How many seats are at this table, humans and bots together. The host
    // sets it; bots fill whatever the humans don't take.
    //
    // Matt asked for this after the first online game: "I don't see a way to
    // change the number of total players so it just seems to be set to three
    // total... I should be able to set how many I want and then we can
    // include bots even if we have more than three humans."
    //
    // Clamped here, not just in the UI - this is the real boundary a
    // malformed client would hit.
    tableSize: clampTableSize(tableSize),
    playerSlots: [{
      uid: user.uid,
      displayName: user.displayName || 'Host',
      seatIndex: 0,
    }],
  };

  await set(dbRef(db, `${GAMES}/${gameId}/meta`), meta);
  await set(dbRef(db, `${CODES}/${code}`), gameId);
  await set(dbRef(db, `${USER_GAMES}/${user.uid}/${gameId}`), { role: 'host', joinedAt: meta.createdAt });

  return { gameId, code };
};

export const joinRoom = async ({ user, code }) => {
  if (!db) throw new Error('Online play needs Firebase configured (see .env.example).');
  if (!user) throw new Error('Sign in first.');

  const normalized = normalizeRoomCode(code);
  const codeSnap = await get(dbRef(db, `${CODES}/${normalized}`));
  if (!codeSnap.exists()) throw new Error(`No game found for code "${normalized}".`);
  const gameId = codeSnap.val();

  const metaSnap = await get(dbRef(db, `${GAMES}/${gameId}/meta`));
  if (!metaSnap.exists()) throw new Error('That game no longer exists.');
  const meta = metaSnap.val();

  const slots = meta.playerSlots || [];
  const alreadyIn = slots.some((s) => s.uid === user.uid);

  // Rejoining a game you're already seated in is always allowed, even after
  // it has started - that IS the reclaim-your-seat path for someone who
  // dropped out and came back.
  if (!alreadyIn) {
    if (meta.status !== 'lobby') throw new Error('That game has already started.');
    if (slots.length >= (meta.tableSize || meta.maxPlayers || MAX_PLAYERS)) throw new Error('That game is full.');
    slots.push({
      uid: user.uid,
      displayName: user.displayName || 'Guest',
      seatIndex: slots.length,
    });
    await update(dbRef(db, `${GAMES}/${gameId}/meta`), { playerSlots: slots, lastActiveAt: Date.now() });
    await set(dbRef(db, `${USER_GAMES}/${user.uid}/${gameId}`), { role: 'guest', joinedAt: new Date().toISOString() });
  }

  return { gameId };
};

export const setTableSize = async ({ user, gameId, meta, tableSize }) => {
  if (!db) throw new Error('Online play needs Firebase configured.');
  if (meta.hostUid !== user?.uid) throw new Error('Only the host can change the table size.');
  if (meta.status !== 'lobby') throw new Error('The game has already started.');
  const seated = (meta.playerSlots || []).length;
  await update(dbRef(db, `${GAMES}/${gameId}/meta`), {
    tableSize: clampTableSize(Math.max(tableSize, seated)),
  });
};

export const leaveLobby = async ({ user, gameId }) => {
  if (!db || !user || !gameId) return;
  const metaSnap = await get(dbRef(db, `${GAMES}/${gameId}/meta`));
  if (!metaSnap.exists()) return;
  const meta = metaSnap.val();
  if (meta.status !== 'lobby') return; // mid-game seats are kept, not vacated
  const slots = (meta.playerSlots || [])
    .filter((s) => s.uid !== user.uid)
    .map((s, i) => ({ ...s, seatIndex: i }));
  await update(dbRef(db, `${GAMES}/${gameId}/meta`), { playerSlots: slots });
  await remove(dbRef(db, `${GAMES}/${gameId}/presence/${user.uid}`));
};

/**
 * Host-only: turns the lobby's seats into a real game.
 *
 * Bots top the table up to the legal minimum, plus however many the host
 * asked for - so two friends can start immediately rather than waiting for a
 * third (Matt's call).
 */
export const startRoom = async ({ user, gameId, meta }) => {
  if (!db) throw new Error('Online play needs Firebase configured.');
  if (meta.hostUid !== user?.uid) throw new Error('Only the host can deal.');

  const seats = [...(meta.playerSlots || [])].sort((a, b) => a.seatIndex - b.seatIndex);
  if (seats.length === 0) throw new Error('Nobody is in the room.');

  const humans = seats.map((s) => ({ id: s.uid, name: s.displayName, isBot: false }));
  // The host's chosen table size, but never fewer seats than there are
  // people already sitting down, and never below the rulebook's minimum.
  const total = clampTableSize(Math.max(meta.tableSize || MIN_PLAYERS, humans.length));
  const bots = Array.from({ length: Math.max(0, total - humans.length) }, (_, i) => ({
    id: `bot${i}`,
    name: PERSONALITIES[personalityFor(i)].label,
    isBot: true,
    botIndex: i,
  }));

  const players = [...humans, ...bots];
  // Host-authoritative seed, so every client deals the identical game from
  // the snapshot it receives rather than each shuffling its own.
  const seed = Math.floor(Math.random() * 2 ** 31);
  const gameState = createGameState({ players, seed });
  // createGameState only copies the fields it knows about; botIndex has to be
  // carried across by hand so personalityForSeat can find it later.
  gameState.players = gameState.players.map((p, i) => (
    players[i].isBot ? { ...p, botIndex: players[i].botIndex } : p
  ));

  await set(dbRef(db, `${GAMES}/${gameId}/state`), encode(gameState));
  await update(dbRef(db, `${GAMES}/${gameId}/meta`), {
    status: 'in-progress',
    lastActiveAt: Date.now(),
    // An async room says who it waits on from the first turn (asyncPlay.js).
    ...(meta.async ? waitingPatch(gameState, meta, asyncAdapter) : {}),
  });
  return gameState;
};

/**
 * Applies one action to the shared state, but only if nobody else got there
 * first.
 *
 * The transaction re-reads whatever is actually stored and re-runs the
 * reducer against THAT, aborting if the sequence number has moved on since
 * `expectedSeq`. Two clients that briefly disagree about who should be
 * driving therefore can't both land the same move: the loser's transaction
 * sees a bumped seq and gives up.
 */
export const commitAction = async ({ gameId, expectedSeq, apply }) => {
  if (!db) throw new Error('Online play needs Firebase configured.');
  const result = await runTransaction(dbRef(db, `${GAMES}/${gameId}/state`), (raw) => {
    const current = decode(raw);
    if (!current) return raw;
    if (seqOf(current) !== expectedSeq) return undefined; // abort: someone else moved
    const next = apply(current);
    if (!next || seqOf(next) === seqOf(current)) return undefined; // no-op action
    return encode(next);
  });
  return { committed: result.committed, state: decode(result.snapshot.val()) };
};

/**
 * Deletes the caller's own dead rooms.
 *
 * Deliberately self-service rather than a global sweep, because that is what
 * the security rules actually permit: `.read` is granted per room on
 * meta/state/presence and NOT on the collection, so no client can list every
 * room; and room deletion is the host's alone. A client can, however, read
 * `cantStopUserGames/<uid>` - the rooms it has been in - and delete the ones
 * it hosts. Everyone tidying up after themselves covers the whole database
 * without anyone needing to see anyone else's games.
 *
 * (The alternative was a scheduled Cloud Function, which needs the Firebase
 * project on the paid plan.)
 *
 * Also prunes userGames pointers to rooms that are already gone - litter left
 * behind when somebody else's room was cleaned up.
 *
 * Entirely best-effort: it runs in the background when the lobby opens and
 * every failure is swallowed. Nothing about starting or joining a game should
 * ever wait on housekeeping.
 */
// `database` is injectable purely so the live integration test can run the
// real function as a real signed-in user - the module-level `db` belongs to
// the app's own Firebase instance, which has nobody signed into it under test.
export const sweepMyRooms = async ({ user, now = Date.now(), database = db }) => {
  if (!database || !user) return { deleted: 0, pruned: 0 };
  let deleted = 0;
  let pruned = 0;

  const mine = await get(dbRef(database, `${USER_GAMES}/${user.uid}`));
  if (!mine.exists()) return { deleted, pruned };

  for (const [gameId, entry] of Object.entries(mine.val() || {})) {
    try {
      // eslint-disable-next-line no-await-in-loop -- a handful of rooms, and
      // serial keeps the failure of one from cancelling the rest.
      const metaSnap = await get(dbRef(database, `${GAMES}/${gameId}/meta`));
      if (!metaSnap.exists()) {
        // eslint-disable-next-line no-await-in-loop
        await remove(dbRef(database, `${USER_GAMES}/${user.uid}/${gameId}`));
        pruned += 1;
        continue;
      }
      if (entry?.role !== 'host' && metaSnap.val().hostUid !== user.uid) continue;

      // eslint-disable-next-line no-await-in-loop
      const presenceSnap = await get(dbRef(database, `${GAMES}/${gameId}/presence`));
      const meta = metaSnap.val();
      if (!isDeadRoom(meta, presenceSnap.val() || {}, now)) continue;

      // Room first: a code may only be released once it points at nothing.
      // eslint-disable-next-line no-await-in-loop
      await remove(dbRef(database, `${GAMES}/${gameId}`));
      if (meta.roomCode) {
        // eslint-disable-next-line no-await-in-loop
        await remove(dbRef(database, `${CODES}/${meta.roomCode}`)).catch(() => {});
      }
      // eslint-disable-next-line no-await-in-loop
      await remove(dbRef(database, `${USER_GAMES}/${user.uid}/${gameId}`)).catch(() => {});
      deleted += 1;
    } catch {
      // A room someone else now owns, a permission change, a dropped
      // connection - none of it should stop the rest of the sweep.
    }
  }
  return { deleted, pruned };
};

export const bindRoom = ({ gameId, user, onMeta, onState, onPresence }) => {
  if (!db) throw new Error('Online play needs Firebase configured.');

  const metaRef = dbRef(db, `${GAMES}/${gameId}/meta`);
  const stateRef = dbRef(db, `${GAMES}/${gameId}/state`);
  const presenceRef = dbRef(db, `${GAMES}/${gameId}/presence`);

  const offMeta = onValue(metaRef, (snap) => onMeta(snap.val()));
  const offState = onValue(stateRef, (snap) => onState(decode(snap.val())));
  const offPresence = onValue(presenceRef, (snap) => onPresence(snap.val() || {}));

  // Let Firebase itself clear our presence the instant the connection drops -
  // a closed tab, a dead network, a crashed browser. Far more reliable than
  // an explicit "I'm leaving" write, which a force-quit never gets to send.
  let myPresenceRef = null;
  if (user) {
    myPresenceRef = dbRef(db, `${GAMES}/${gameId}/presence/${user.uid}`);
    onDisconnect(myPresenceRef).remove();
    set(myPresenceRef, true);
    // Opening a room counts as activity, so a game people keep coming back to
    // never ages into the cleanup window.
    update(dbRef(db, `${GAMES}/${gameId}/meta`), { lastActiveAt: Date.now() }).catch(() => {});
  }

  return () => {
    offMeta();
    offState();
    offPresence();
    if (myPresenceRef) remove(myPresenceRef);
  };
};
